import {
  Box,
  Button,
  FormikSelectInputNative,
  FormikTextareaInput,
  FormikTextInput,
  Modal,
  SelectInput,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Formik, FormikHelpers, FormikValues } from 'formik';
import { useMemo } from 'react';
import { Form } from 'react-router-dom';
import * as yup from 'yup';

import {
  FlagTypeLabels,
  MilestoneRequirement,
  ProgramType,
  ProgramTypeLabels,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsType,
  RequirementFlagSettingPostRequest,
} from 'types';

import { isErrorWithData } from '../../services/helpers';
import {
  useCreateRequirementFlagSettingMutation,
  useGetRequirementFlagSettingsQuery,
} from '../../services/requirementFlagSettings';

import { ReviewPackageLabels } from './RequirementFlagSettings';

const flagSettingSchema = yup.object().shape({
  baseCategory: yup.string().required('Section is a required field.'),
  description: yup.string().required('Description is a required field.'),
  meetsAccountRequirements: yup
    .array()
    .min(1, 'At least one review package must be selected.')
    .required('Found In is a required field.'),
  name: yup.string().required('Name is a required field.'),
  programTypes: yup
    .array()
    .min(1, 'At least one Program Type must be selected.')
    .required('Program Types is a required field.'),
  type: yup.string().required('Type is a required field.'),
});

export const CreateFlagSettingModal = ({ isOpen, handleClose }: { isOpen: boolean; handleClose: () => void }) => {
  const [createFlagSetting] = useCreateRequirementFlagSettingMutation();
  const { data: existingRequirementFlagSettings, isLoading } = useGetRequirementFlagSettingsQuery({
    type: undefined,
  });

  const typesAvailableForCreation = useMemo(() => {
    if (!isLoading && existingRequirementFlagSettings?.requirementFlagSettings) {
      return Object.values(RequirementFlagSettingsType).filter((type) => {
        const alreadyExists = existingRequirementFlagSettings.requirementFlagSettings?.some(
          (setting: RequirementFlagSettingsDocument) => setting.type === type,
        );
        return !alreadyExists;
      });
    }
    return [];
  }, [existingRequirementFlagSettings, isLoading]);

  const flagFoundInPackageOptions = [
    MilestoneRequirement.installPackage,
    MilestoneRequirement.activationPackage,
    MilestoneRequirement.postActivationPackage,
  ].map((requirement) => ({ value: requirement, label: ReviewPackageLabels[requirement] }));

  type FlagSettingFormSubmission = {
    meetsAccountRequirements: MilestoneRequirement[] | [];
    name: string;
    description: string;
    baseCategory: string;
    type: RequirementFlagSettingsType | null;
    programTypes: string[];
  };

  const initialValues: FlagSettingFormSubmission = useMemo(
    () => ({
      dataKey: '',
      baseCategory: '',
      meetsAccountRequirements: [],
      name: '',
      description: '',
      type: null,
      programTypes: [],
    }),
    [],
  );

  const handleUpdate = async (data: RequirementFlagSettingPostRequest) => {
    try {
      await createFlagSetting({
        body: data,
      }).unwrap();
      toast.success('Flag setting created');
      handleClose();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error('Error creating flag Setting');
      }
    }
  };

  const handleSubmit = async (values: FormikValues, { resetForm }: FormikHelpers<FlagSettingFormSubmission>) => {
    let data = {
      baseCategory: values.baseCategory,
      dataKey: values.dataKey,
      description: values.description,
      meetsAccountRequirements: values.meetsAccountRequirements.map(
        (option: { label: string; value: string }) => option.value,
      ),
      name: values.name,
      programTypes: values.programTypes.map((option: { label: string; value: string }) => option.value),
      type: values.type,
    } as RequirementFlagSettingPostRequest;

    await handleUpdate(data);
    resetForm();
  };

  return (
    <Formik
      validationSchema={flagSettingSchema}
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={(values, formikHelpers) => {
        return handleSubmit(values, formikHelpers);
      }}
    >
      {({ errors, handleBlur, isSubmitting, resetForm, setFieldValue, submitForm, touched, values }) => {
        return (
          <Form noValidate>
            <Modal
              isOpen={isOpen}
              ariaLabelledBy="createSettingHeader"
              fullScreenMobile
              onDismiss={() => {
                resetForm();
                handleClose();
              }}
              maxWidth="500px"
            >
              <Modal.Header
                id="createSettingHeader"
                title="Create Requirement Setting"
                onDismiss={() => {
                  resetForm();
                  handleClose();
                }}
              />
              <Modal.Body background="secondary" childGap="lg">
                <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                  <Field
                    label="Type"
                    name="type"
                    id="type"
                    options={
                      typesAvailableForCreation?.map((type: RequirementFlagSettingsType) => ({
                        label: `${FlagTypeLabels[type]} (${type})`,
                        value: type,
                      })) ?? []
                    }
                    component={FormikSelectInputNative}
                    isRequired
                    error={touched.type && (errors.type as string)}
                  />
                  <SelectInput
                    id="programTypes"
                    name="programTypes"
                    label="Program Types"
                    value={values.programTypes}
                    onChange={(event) => {
                      setFieldValue('programTypes', event.target.value);
                    }}
                    options={Object.values(ProgramType).map((programType) => ({
                      label: ProgramTypeLabels[programType],
                      value: programType,
                    }))}
                    onBlur={handleBlur}
                    isMulti
                    isClearable
                    isRequired
                    error={touched.programTypes && (errors.programTypes as string)}
                    menuPortalTarget={document.body}
                  />
                  <Field
                    type="text"
                    label="Name"
                    name="name"
                    id="name"
                    component={FormikTextInput}
                    isRequired
                    error={touched.name && (errors.name as string)}
                    placeholder="Corresponds to the item flagged"
                  />
                  <Field
                    type="text"
                    label="Description"
                    name="description"
                    id="description"
                    component={FormikTextareaInput}
                    isRequired
                    error={touched.description && (errors.description as string)}
                    placeholder="Describe the flag purpose"
                  />
                  <Field
                    type="text"
                    label="Data Key"
                    name="dataKey"
                    id="dataKey"
                    component={FormikTextInput}
                    placeholder="Optional flag identifier"
                  />
                  <Field
                    type="text"
                    label="Section"
                    name="baseCategory"
                    id="baseCategory"
                    component={FormikTextInput}
                    isRequired
                    error={touched.baseCategory && (errors.baseCategory as string)}
                    placeholder="Label of review package section (ie. Equipment & Materials)"
                  />
                  <SelectInput
                    id="meetsAccountRequirements"
                    name="meetsAccountRequirements"
                    label="Found In"
                    value={values.meetsAccountRequirements}
                    onChange={(event) => {
                      setFieldValue('meetsAccountRequirements', event.target.value);
                    }}
                    options={flagFoundInPackageOptions}
                    onBlur={handleBlur}
                    isMulti
                    isClearable
                    isRequired
                    error={touched.meetsAccountRequirements && (errors.meetsAccountRequirements as string)}
                    menuPortalTarget={document.body}
                  />
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <>
                  <Button
                    variant="secondary"
                    tone="neutral"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    onClick={() => {
                      resetForm();
                      handleClose();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    type="submit"
                    onClick={submitForm}
                  >
                    Create
                  </Button>
                </>
              </Modal.Footer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
